// Mini modal with picture separated by a vertical line
import React, { useContext, useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { type RestFortySixtyMiniModal1Modal } from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import { RefsContext } from '@bbkFunnelUtils/refs.context';
import {
  Avatar,
  ModalDesktopInnerSpacer,
  ModalMobileInnerSpacer,
  OfferTitleMobile,
  TitleTheme,
  useMediaQueries,
} from '../sections';

import './modal.scss';

type Props = {
  modalInfo: RestFortySixtyMiniModal1Modal;
  close: () => void;
  detailedAction: React.ReactElement;
};

export const FortySixtyMiniModal1V2: React.FC<Props> = ({
  modalInfo: {
    id,
    requires: {
      info: { title, details },
      person,
    },
  },
  close,
  detailedAction,
}) => {
  const { mobileView } = useMediaQueries();

  const refsPrefix = id;
  const refsCtx = useContext(RefsContext);
  const titleRef = useRef<HTMLSpanElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-title`, titleRef), [titleRef]);
  const imageRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-image`, imageRef), [imageRef]);
  const personRef = useRef<HTMLDivElement>(null);
  useEffect(
    () => refsCtx?.addRef(`${refsPrefix}-person`, personRef),
    [personRef]
  );
  const descRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-desc`, descRef), [descRef]);
  const actionsRef = useRef<HTMLDivElement>(null);
  useEffect(
    () => refsCtx?.addRef(`${refsPrefix}-actions`, actionsRef),
    [actionsRef]
  );

  if (mobileView) {
    return (
      <ModalMobileInnerSpacer
        className={cs(
          'forty-sixty-mini-1',
          'display-grid',
          'mobile',
          'inner-spacer'
        )}
      >
        <OfferTitleMobile title={<TitleTheme title={title} />} close={close} />
        <div
          css={css`
            border-bottom: 1px solid #e5e5e5;
            padding-bottom: 1rem;
            margin-bottom: 1rem;
          `}
        >
          <Avatar person={person} />
        </div>
        <div>
          <CustomerHtml value={details} />
        </div>
        <div>{detailedAction}</div>
      </ModalMobileInnerSpacer>
    );
  }

  return (
    <ModalDesktopInnerSpacer
      css={css`
        max-width: 600px;
      `}
      className={cs(
        'forty-sixty-mini-1',
        'desktop',
        'display-grid',
        'inner-spacer'
      )}
    >
      <OfferTitleMobile
        title={<TitleTheme title={title} ref={titleRef} />}
        close={close}
      />
      <div
        className={cs('display-grid', 'content-grid', 'align-items--center')}
      >
        <div
          css={css`
            border-right: 1px solid #e5e5e5;
            padding-right: 1rem;
          `}
        >
          <div ref={personRef}>
            <Avatar person={person} vertical />
          </div>
        </div>
        <div
          css={css`
            padding-left: 2rem;
          `}
        >
          <div
            css={css`
              margin-bottom: 1.5rem;
              overflow-y: auto;
              max-height: 170px;
            `}
            ref={descRef}
          >
            <CustomerHtml value={details} />
          </div>
          <div ref={actionsRef}>{detailedAction}</div>
        </div>
      </div>
    </ModalDesktopInnerSpacer>
  );
};
